import FormImageInput from "@/components/forms/FormImageInput";
import FormInput from "@/components/forms/FormInput";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { useCreatePopularSearchMutation } from "@/app/features/popularSearch/popularSearchApiSlice";

const initialValues = {
  name: "",
  tileHeader: "Buying guide",
  articleUrl: "",
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required().min(2).max(50).label("Name").trim(),
  tileHeader: Yup.string().max(40).label("Tile header").trim(),
  articleUrl: Yup.string()
    .trim()
    .required("Guide article URL is required")
    .test(
      "valid-article-url",
      "Enter a full URL or a path such as /guides/small-suvs",
      (value) => {
        if (!value) return false;
        if (value.startsWith("/")) return value.length > 1;
        try {
          const parsed = new URL(value);
          return parsed.protocol === "http:" || parsed.protocol === "https:";
        } catch {
          return false;
        }
      }
    )
    .label("Guide article URL"),
  image: Yup.mixed()
    .required("Image is required")
    .test(
      "fileType",
      "Only the following formats are accepted: .jpg, .jpeg, .png, .webp",
      (value) =>
        !value ||
        (value &&
          ["image/png", "image/jpg", "image/jpeg", "image/webp"].includes(
            value.type
          ))
    )
    .label("Image"),
});

export default function CreatePopularSearchPage() {
  const [createPost, { isLoading }] = useCreatePopularSearchMutation();

  const navigate = useNavigate();

  const handleSubmit = async (inputs) => {
    try {
      const formData = new FormData();
      formData.append("name", inputs.name);
      formData.append("tileHeader", inputs.tileHeader || "Buying guide");
      formData.append("articleUrl", inputs.articleUrl || "");
      formData.append("type", "Category");

      if (inputs.image) {
        formData.append("image", inputs.image);
      }

      const result = await createPost(formData);

      if (result && result.data) {
        toast.success(result.data.message);
        navigate("/popular-search");
      } else if (result && result.error) {
        toast.error(result.error.data.message);
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: 8,
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" spacing={4}>
            <Typography variant="h4">Create Buying Guide</Typography>
          </Stack>

          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {({
              setFieldValue,
              values,
              setFieldTouched,
              setFieldError,
            }) => (
              <Form>
                <Stack spacing={3}>
                  <FormInput
                    label="Name"
                    name="name"
                    type="text"
                    placeholder="e.g. Small SUVs"
                  />
                  <FormInput
                    label="Tile header"
                    name="tileHeader"
                    type="text"
                    placeholder="Buying guide"
                  />
                  <FormInput
                    label="Guide article URL"
                    name="articleUrl"
                    type="text"
                    placeholder="https://thebeep.com.au/guides/your-guide-slug"
                  />
                  <FormImageInput
                    name="image"
                    imageValues={values.image}
                    onChangeSelectedImage={(event) => {
                      setFieldTouched(event.target.name);
                      setFieldValue(event.target.name, event.target.files[0]);
                    }}
                    onError={(message) => {
                      setFieldError("image", message);
                    }}
                    onCheckRes={(checkResolution) => {
                      setFieldValue("checkResolution", checkResolution);
                    }}
                    checkResolution={values.checkResolution}
                    label="Image"
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                  >
                    Submit
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </Container>
    </Box>
  );
}
